import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarPlus, Pause, Play } from "lucide-react";
import { PATREON_URL } from "@/lib/club";
import { upcomingLives } from "@/lib/schedule";

/* A text thread that can carry more than words: photos, a voice note, a class
   invite, and a link card. Shared by the round-4 heroes and the page draft. */

export type Msg =
  | { kind: "text"; text: string; react?: string }
  | { kind: "photo"; src: string; alt: string; caption?: string; react?: string }
  | { kind: "voice"; length: string; transcript: string }
  | { kind: "invite" }
  | { kind: "link" };

const EASE = [0.22, 1, 0.36, 1] as const;

/* ─── Real class schedule: 2nd & 4th Tuesdays, 7 PM Mountain ─── */

const DENVER = "America/Denver";

/** The next club (Patreon) classes; see lib/schedule for the rule. */
export function nextClasses(count = 2, from = new Date()) {
  return upcomingLives(count, "club", from).map(l => l.start);
}

export const classDay = (d: Date) => d.toLocaleDateString("en-US", { timeZone: DENVER, weekday: "short", month: "short", day: "numeric" });

/** A calendar file for the class. Length is a placeholder until Chelsea confirms it. */
function icsHref(start: Date) {
  const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const end = new Date(start.getTime() + 90 * 60e3);
  const body = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//A Few Loose Threads//Club//EN", "BEGIN:VEVENT",
    `UID:ltc-${stamp(start)}@afewloosethreads.com`, `DTSTAMP:${stamp(new Date())}`,
    `DTSTART:${stamp(start)}`, `DTEND:${stamp(end)}`,
    "SUMMARY:Loose Threads Club · live class with Chelsea",
    `DESCRIPTION:Members join live on Patreon. Not a member yet? ${PATREON_URL}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(body)}`;
}

/* ─── Message bodies ─── */

function Voice({ length, transcript }: { length: string; transcript: string }) {
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => setPlaying(false), 4000);
    return () => clearTimeout(t);
  }, [playing]);
  return <div className="r4-voice">
    <div className="r4-voice-row">
      <button type="button" onClick={() => setPlaying(p => !p)} aria-label={playing ? "Pause voice note" : "Play voice note"}>
        {playing ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
      </button>
      <span className={`r4-wave${playing ? " is-playing" : ""}`} aria-hidden="true">
        {[5, 9, 14, 8, 17, 11, 6, 13, 18, 9, 12, 7, 15, 10, 6, 12, 16, 8, 5, 10].map((h, i) => <i key={i} style={{ height: h, animationDelay: `${i * 0.05}s` }} />)}
      </span>
      <span className="r4-voice-len">{length}</span>
    </div>
    <p className="r4-voice-text">“{transcript}”</p>
  </div>;
}

function Invite() {
  const [next] = nextClasses(1);
  return <div className="r4-invite">
    <div className="r4-invite-date" aria-hidden="true">
      <span>{next.toLocaleDateString("en-US", { timeZone: DENVER, month: "short" })}</span>
      <b>{next.toLocaleDateString("en-US", { timeZone: DENVER, day: "numeric" })}</b>
    </div>
    <div className="r4-invite-body">
      <p className="r4-invite-kicker">Live class</p>
      <p className="r4-invite-title">Sew with Chelsea</p>
      <p className="r4-invite-when">{classDay(next)} · 7 PM MT</p>
    </div>
    <a className="r4-invite-add" href={icsHref(next)} download="loose-threads-class.ics"><CalendarPlus size={14} aria-hidden="true" /> Add</a>
  </div>;
}

function LinkCard() {
  return <a className="r4-link" href={PATREON_URL} target="_blank" rel="noreferrer">
    <img src="/chelsea-rainbow-portrait.jpg" alt="" />
    <span>
      <b>Loose Threads Club</b>
      <span>$25/month · two live classes + every replay</span>
      <small>patreon.com</small>
    </span>
  </a>;
}

function Body({ m }: { m: Msg }) {
  switch (m.kind) {
    case "text": return <>{m.text}</>;
    case "photo": return <figure className="r4-photo"><img src={m.src} alt={m.alt} />{m.caption && <figcaption>{m.caption}</figcaption>}</figure>;
    case "voice": return <Voice length={m.length} transcript={m.transcript} />;
    case "invite": return <Invite />;
    case "link": return <LinkCard />;
  }
}

/* ─── Pacing ─── */

const typeTime = (m: Msg) => (m.kind === "text" ? 420 + Math.min(m.text.length * 7, 520) : 760);

function useSequence(msgs: readonly Msg[], start: boolean, onStep?: (n: number) => void) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? msgs.length : 1);
  const [typing, setTyping] = useState(false);
  const [run, setRun] = useState(0);
  const step = useRef(onStep);
  step.current = onStep;
  useEffect(() => {
    if (reduce || !start) return;
    setShown(1); step.current?.(1);
    let n = 1, t: ReturnType<typeof setTimeout>;
    const next = () => {
      if (n >= msgs.length) return;
      setTyping(true);
      t = setTimeout(() => {
        n += 1; setTyping(false); setShown(n); step.current?.(n);
        t = setTimeout(next, msgs[n - 1].kind === "text" ? 300 : 900);
      }, typeTime(msgs[n]));
    };
    t = setTimeout(next, 650);
    return () => clearTimeout(t);
  }, [msgs, run, reduce, start]);
  return { shown, typing, done: shown >= msgs.length, replay: () => setRun(r => r + 1) };
}

/* ─── Thread ─── */

export function RichThread({ msgs, className = "", onStep, start = true, replies = true, sender = true }: {
  msgs: readonly Msg[]; className?: string; onStep?: (n: number) => void; start?: boolean; replies?: boolean; sender?: boolean;
}) {
  const { shown, typing, done, replay } = useSequence(msgs, start, onStep);
  const win = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const [offset, setOffset] = useState(0);

  // Grow top-down; once the thread outgrows its window, glide it up as one piece.
  useLayoutEffect(() => {
    const w = win.current, l = list.current;
    if (!w || !l) return;
    const measure = () => setOffset(Math.max(0, l.scrollHeight - w.clientHeight));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(l); ro.observe(w);
    return () => ro.disconnect();
  }, []);

  return <div className={`r4-thread ${className}`}>
    {sender && <p className="r4-sender"><img src="/img/chelsea.jpeg" alt="" /><b>Chelsea</b><span>Loose Threads Club</span></p>}
    <div className={`r4-window${offset > 0 ? " is-scrolled" : ""}`} ref={win}>
      <motion.ol ref={list} className="r4-list" aria-live="polite" aria-label="Messages from Chelsea" animate={{ y: -offset }} transition={{ duration: 0.55, ease: EASE }}>
        {msgs.slice(0, shown).map((m, i) => (
          <motion.li key={i} className={`r4-msg r4-msg-${m.kind}${i === shown - 1 && !typing ? " r4-tail" : ""}`}
            initial={i === 0 ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.32, ease: EASE }}>
            <Body m={m} />
            {"react" in m && m.react && <motion.span className="r4-react" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.1, type: "spring", stiffness: 500, damping: 18 }} aria-label={`Reacted ${m.react}`}>{m.react}</motion.span>}
          </motion.li>
        ))}
        {typing && <motion.li key={`t${shown}`} className="r4-msg r4-tail r4-typing" aria-label="Chelsea is typing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18 }}><i /><i /><i /></motion.li>}
        {done && replies && <motion.li key="replies" className="r4-replies" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.35, ease: EASE }}>
          <a className="r4-reply r4-reply-yes" href={PATREON_URL} target="_blank" rel="noreferrer">Okay, I’m in</a>
          <Link className="r4-reply" to="/loose-threads-club">Tell me more</Link>
        </motion.li>}
      </motion.ol>
    </div>
    {done && <button type="button" className="r4-replay" onClick={replay}>↻ Replay</button>}
  </div>;
}

/** A visitor question + Chelsea's answer, used between sections of the page draft. */
export function Ask({ q, children }: { q: string; children: ReactNode }) {
  return <motion.div className="r4-ask" initial="hide" whileInView="show" viewport={{ once: true, margin: "-120px" }}>
    <motion.p className="r4-ask-q" variants={{ hide: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: EASE } } }}>{q}</motion.p>
    <div className="r4-ask-a">
      <img src="/img/chelsea.jpeg" alt="" />
      <span className="r4-ask-slot">
        <motion.span className="r4-msg r4-typing" variants={{ hide: { opacity: 0 }, show: { opacity: [0, 1, 1, 0], transition: { duration: 1.1, times: [0, .15, .85, 1], delay: 0.4 } } }} aria-hidden="true"><i /><i /><i /></motion.span>
        <motion.span className="r4-msg r4-tail r4-ask-text" variants={{ hide: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0, transition: { duration: 0.32, delay: 1.45, ease: EASE } } }}>{children}</motion.span>
      </span>
    </div>
  </motion.div>;
}
